import { FC, useContext, useState } from 'react';
import { Button, Chip, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  EditOutlined,
  DeleteOutlined,
  CalendarTodayOutlined,
  GroupOutlined,
  AdsClick,
  ExpandLess,
  ExpandMore,
  Link,
  MenuBook,
  FiberManualRecord,
  DescriptionOutlined,
  StarBorder,
} from '@mui/icons-material';
import { format } from 'date-fns';

import { useRouter } from '@cvt/routing';

import { BoxWell } from '@shared/components/BoxWell';
import { useDictionary } from '@cvt/hooks/useDictionary';
import { DialogContext } from '@cvt/contexts';
import { MeetingStatusChip } from './MeetingStatusChip';


type Props = Meetings.Meeting & {
  onDelete: (id: number) => void;
  showFeedback: boolean;
};

export const MeetingListItem: FC<Props> = (props) => {
  const { onDelete, showFeedback, ...meeting } = props;
  const dictionary = useDictionary();
  const { openDialog } = useContext(DialogContext);

  const [isExpanded, setIsExpanded] = useState(false);

  const openMeetingFeedbackDialog = (meeting: { id: number; feedback?: { id?: number } } | undefined, openDialog: any) => {
    if (!meeting) return;
    if (meeting.feedback?.id) {
      openDialog('editMeetingFeedback', { meetingId: meeting.id, defaultValues: meeting.feedback ?? undefined });
    } else {
      openDialog('createMeetingFeedback', {
        meetingId: meeting.id,
      });
    }
  };

  const router = useRouter();
  return (
    <BoxWell
      sx={{ p: 2, height: '100%' }}
    >
      <Stack direction="row" gap={1} alignItems="center">
        <CalendarTodayOutlined color="primary" sx={{ fontSize: 16 }}/>
        <Typography variant="body2" fontWeight={500} color="primary">
          {meeting.date ? format(meeting.date, 'MMMM dd, yyyy') : 'No date set'}
        </Typography>
        <FiberManualRecord sx={{ fontSize: 10, color: 'text.secondary' }}/>
        <Tooltip title={`Number of linked stories: ${meeting.storiesThrough?.length || 0}`}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <DescriptionOutlined sx={{ fontSize: 16, color: 'text.secondary' }}/>
            <Typography variant="body2" fontWeight={500} color="primary">
              {meeting.storiesThrough?.length}
            </Typography>
          </Stack>
        </Tooltip>
      </Stack>

      <Grid container spacing={2} mt={0.5}>
        <Grid container size={{ xs: 12 }} direction={{ xs: 'column-reverse', sm: 'row' }}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <Typography variant="body1" fontWeight={600} fontSize={18}>
              {meeting.title}
            </Typography>
            {!isExpanded && (
              <Typography variant="body2" fontWeight={500} color="text.secondary">
                {meeting.audience}
              </Typography>
            )}
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={2}>
              {meeting?.feedback && showFeedback && (
                <Chip
                  icon={<StarBorder sx={{ fontSize: 16, color: 'gold', mr: 0.5 }}/>}
                  label={`${meeting?.feedback.score}/10`}
                  color="secondary"
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    color: 'black',
                  }}
                />
              )}
              {showFeedback && (
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  onClick={() => openMeetingFeedbackDialog(meeting, openDialog)}
                  endIcon={<StarBorder/>}>
                    Feedback
                </Button>
              )}
              <MeetingStatusChip status={meeting.status as Meetings.MeetingStatus}/>
              <IconButton
                onClick={(event) => [event.stopPropagation(), event.preventDefault(), setIsExpanded(!isExpanded)]}
                color="primary"
                size="small"
              >
                {isExpanded ? <ExpandLess/> : <ExpandMore/>}
              </IconButton>
            </Stack>
          </Grid>
        </Grid>

        {isExpanded && (
          <Grid size={{ xs: 12 }}>
            <Stack>
              <Grid container spacing={2} mt={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <BoxWell sx={{ borderColor: '#f1f2f4' }}>
                    <Stack direction="row" gap={1}>
                      <GroupOutlined color="primary" sx={{ fontSize: 20 }}/>
                      <Stack gap={1} justifyContent="center">
                        <Typography variant="body2" fontWeight={600} color="text.secondary" fontSize={12}>
                          {dictionary.meetings.list.audience}
                        </Typography>
                        <Typography variant="body2" color={meeting.audience ? 'text.primary' : 'text.secondary'} fontStyle={meeting.audience ? 'normal' : 'italic'}>
                          {meeting.audience || 'No audience specified'}
                        </Typography>
                      </Stack>
                    </Stack>
                  </BoxWell>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <BoxWell sx={{ borderColor: '#f1f2f4' }}>
                    <Stack direction="row" gap={1}>
                      <AdsClick color="warning" sx={{ fontSize: 20 }}/>
                      <Stack gap={1} justifyContent="center">
                        <Typography variant="body2" fontWeight={600} color="text.secondary" fontSize={12}>
                          {dictionary.meetings.list.desiredOutcome}
                        </Typography>
                        <Typography variant="body2" color={meeting.desiredOutcome ? 'text.primary' : 'text.secondary'} fontStyle={meeting.desiredOutcome ? 'normal' : 'italic'}>
                          {meeting.desiredOutcome || 'No desired outcome specified'}
                        </Typography>
                      </Stack>
                    </Stack>
                  </BoxWell>
                </Grid>
                {meeting?.feedback && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <BoxWell sx={{ borderColor: '#f1f2f4' }}>
                      <Stack direction="row" gap={1}>
                        <StarBorder color="info" sx={{ fontSize: 20 }}/>
                        <Stack gap={1} justifyContent="center">
                          <Typography variant="body2" fontWeight={600} color="text.secondary" fontSize={12}>
                            {dictionary.meetings.list.meetingTakeaway}
                          </Typography>
                          <Typography variant="body2">
                            {meeting.feedback?.reflection}
                          </Typography>
                        </Stack>
                      </Stack>
                    </BoxWell>
                  </Grid>
                )}
              </Grid>

              <Stack mt={2}>
                <Typography variant="body2" fontWeight={600} color="text.secondary" fontSize={12}>
                  {dictionary.meetings.list.tags}
                </Typography>
                <Stack gap={2} direction="row" flexWrap="wrap" mt={1}>
                  {meeting?.tags?.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      color="secondary"
                      sx={{
                        height: 'auto',
                        p: 0.1,
                        fontWeight: 500,
                        '& .MuiChip-label': {
                          whiteSpace: 'wrap',
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Stack>

              <Stack mt={2}>
                <Typography variant="body2" fontWeight={600} color="text.secondary" fontSize={12}>
                  Linked Stories
                </Typography>
                <Stack gap={1} mt={1}>
                  {meeting.storiesThrough && meeting.storiesThrough.length > 0 ? (
                    meeting.storiesThrough.map((storyThrough) => (
                      <Stack direction="row" gap={1} alignItems="center" key={storyThrough.story.id}>
                        <MenuBook color="primary" sx={{ fontSize: 16 }}/>
                        <Typography variant="body2" color="text.secondary">
                          {storyThrough.story.title}
                        </Typography>
                      </Stack>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      No stories linked yet.
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button
              onClick={() => openDialog('meetingLinkStories', { meetingId: meeting.id })}
              color="primary"
              size="small"
              startIcon={<Link/>}
              variant="outlined"
            >
              Link Story
            </Button>
            <Stack direction="row" gap={1} alignItems="center">
              <IconButton
                onClick={(event) => [event.stopPropagation(), event.preventDefault(), router.meetings.edit(meeting.id).go()]}
                color="primary"
                size="small"
              >
                <EditOutlined/>
              </IconButton>
              <IconButton
                onClick={(event) => [event.stopPropagation(), event.preventDefault(), onDelete(meeting.id)]}
                color="error"
                size="small"
              >
                <DeleteOutlined/>
              </IconButton>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </BoxWell>
  );
};
